import { put, select, call } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import { requestPuppy } from '@wix/da-http-client';
import { withOffset } from '../../Stream';
import {
  getCurrentStreamId,
  getCurrentMediaSource,
  getCurrentGallection,
  getCurrentSubfolder,
  getCurrentStreamItems,
  getCurrentTier,
  getGrusername,
} from '../selectors';
import type { Action } from 'redux-actions';
import type { AddDeviationsPayload } from '../actions';
import { getGallectionType } from '../helpers';
import { MediaSourceType } from '../types';
import updateGallectionSize from './updateGallectionSize';
import setGallectionCover from './setGallectionCover';
import type { PapiRequestDaSharedGallectionFoldersCopyDeviations } from '@wix/da-papi-types';
import { normalizeDeviations } from '../../redux/normalizr/helpers';

function* addDeviationsHandler(action: Action<AddDeviationsPayload>): SagaIterator {
  const {
    streamId,
    mediaSource,
    currentTier,
    currentGallection,
    currentSubfolder,
    currentDeviationIds,
    username,
  }: {
    streamId: ReturnType<typeof getCurrentStreamId>;
    mediaSource: ReturnType<typeof getCurrentMediaSource>;
    currentTier: ReturnType<typeof getCurrentTier>;
    currentGallection: ReturnType<typeof getCurrentGallection>;
    currentSubfolder: ReturnType<typeof getCurrentSubfolder>;
    currentDeviationIds: ReturnType<typeof getCurrentStreamItems>;
    username: string;
  } = yield select(state => ({
    streamId: getCurrentStreamId(state),
    mediaSource: getCurrentMediaSource(state),
    currentTier: getCurrentTier(state),
    currentGallection: getCurrentGallection(state),
    currentSubfolder: getCurrentSubfolder(state),
    currentDeviationIds: getCurrentStreamItems(state),
    username: getGrusername(state),
  }));

  // Filter out the deviations we already have on this stream
  const deviations = action.payload.filter(d => !currentDeviationIds.includes(d.deviationId));

  if (!streamId || deviations.length < 1) {
    return;
  }

  const { result: ids, entities } = normalizeDeviations(deviations);
  yield put(
    withOffset.actions.insertItems({
      streamId,
      items: ids,
      entities,
      offset: 0,
    })
  );
  // If this is a non-gallection like those used in journal galleries, we only
  // need to change the stream. Everything after this saves changes on the server
  if (mediaSource === MediaSourceType.CUSTOM_SET) {
    return;
  }

  const type = getGallectionType(mediaSource);
  if (currentGallection && type && type !== 'stash') {
    const targetGallection = currentSubfolder || currentGallection;
    yield call(updateGallectionSize, targetGallection.folderId, deviations.length);

    yield call(setGallectionCover, {
      gallection: targetGallection,
      deviation: deviations[0],
      onlyIfMissing: true,
    });

    const deviationids = deviations.map(deviation => deviation.deviationId);
    const data: PapiRequestDaSharedGallectionFoldersCopyDeviations = {
      username,
      type,
      target_folderid: targetGallection.folderId,
      deviationids,
    };
    yield call(requestPuppy, {
      method: 'post',
      url: 'gallection/folders/copy_deviations',
      data,
    });
  }

  if (currentTier) {
    const tierId = currentTier.deviationId;

    const deviationIds = deviations.map(deviation => deviation.deviationId);
    yield call(requestPuppy, {
      method: 'post',
      url: 'tier/deviation/add',
      data: {
        tier_deviationid: tierId,
        deviationids: deviationIds,
      },
    });
  }
}

export default addDeviationsHandler;
